import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, TextField,
  Typography, Box, CircularProgress, IconButton} from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import { getChatMessages, sendAdminReply, markConversationRead } from '../../services/messageService';
import SendIcon from '@material-ui/icons/Send';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '500px',
    height: '600px',
    
    color: '#f8fafc'
  },
  messageArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: 8 },
  userBubble: {
    alignSelf: 'flex-start',
    background: theme.palette.type === "dark" ? "rgba(30,41,59,0.8)" : "rgba(255,255,255,0.8)",
    border: '1px solid #334155',
    borderRadius: '12px 12px 12px 4px',
    padding: '10px 14px',
    maxWidth: '75%' },
  adminBubble: {
    alignSelf: 'flex-end',
    background: 'rgba(16,185,129,0.15)',
    border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: '12px 12px 4px 12px',
    padding: '10px 14px',
    maxWidth: '75%' },
  userName: { fontSize: '0.7rem', color: theme.palette.text.secondary, marginBottom: 2 },
  messageText: { color: theme.palette.text.primary, fontSize: '0.9rem' },
  timeText: { color: theme.palette.text.disabled, fontSize: '0.7rem', marginTop: 2 },
  inputArea: {
    display: 'flex',
    gap: 8,
    padding: theme.spacing(2),
    borderTop: '1px solid #334155' },
  inputField: {
    flex: 1,
    '& .MuiInputBase-root': { color: '#f8fafc', backgroundColor: theme.palette.type === "dark" ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.6)", borderRadius: 8 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' } },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%' } }));

export default function MessageChatDialog({ open, conversation, onClose }) {
  const classes = useStyles();
  
  const theme = useTheme();
const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && conversation) {
      setLoading(true);
      markConversationRead(conversation.id);
      getChatMessages(conversation.id).then((msgs) => {
        setMessages(msgs);
        setLoading(false);
      });
    }
  }, [open, conversation]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (!conversation) return null;

  const handleSend = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await sendAdminReply(conversation.id, replyText.trim());
      setReplyText('');
      const msgs = await getChatMessages(conversation.id);
      setMessages(msgs);
    } catch (e) {
      console.error('Failed to send reply');
    }
    setSending(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} maxWidth="sm" fullWidth>
      <DialogTitle style={{ borderBottom: '1px solid #334155' }}>
        <Box>
          <Typography>{conversation.userName || 'Unknown User'}</Typography>
          <Typography color="textSecondary" style={{ fontSize: '0.75rem' }}>
            {conversation.userEmail}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={28} />
          </div>
        ) : messages.length === 0 ? (
          <div className={classes.loadingContainer}>
            <Typography style={{ color: theme.palette.text.secondary }}>No messages yet.</Typography>
          </div>
        ) : (
          <div className={classes.messageArea}>
            {messages.map((msg, idx) => {
              const isAdmin = msg.senderId === 'admin';
              return (
                <Box key={msg.id || idx} className={isAdmin ? classes.adminBubble : classes.userBubble}>
                  <Typography className={classes.userName}>
                    {isAdmin ? 'You (Support Team)' : msg.senderName}
                  </Typography>
                  <Typography className={classes.messageText}>{msg.message}</Typography>
                  <Typography className={classes.timeText}>{formatTime(msg.createdAt)}</Typography>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </DialogContent>
      <div className={classes.inputArea}>
        <TextField
          className={classes.inputField}
          variant="outlined"
          size="small"
          placeholder="Type your reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          multiline
          maxRows={3}
        />
        <IconButton style={{ backgroundColor: replyText.trim() ? '#10b981' : '#334155', borderRadius: 8, color: '#fff', width: 40, height: 40 }}
          onClick={handleSend}
          disabled={!replyText.trim() || sending}
        >
          {sending ? <CircularProgress size={18} style={{ color: '#fff' }} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </div>
    </Dialog>
  );
}
